import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { customSwal } from '../CustomSwal';
import { deleteOrphanedImages, formatBytes, scanOrphanedImages } from './consoleMediaApi';

// Admin-only: find images in the Cloudinary console folder that no console item uses, and delete them
const AdminImageCleanup = () => {
  const token = useSelector((state) => state.userLogin.userInfo?.token);
  const [scan, setScan] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const runScan = async () => {
    setScanning(true);
    try {
      const result = await scanOrphanedImages(token);
      setScan(result);
      setSelected(new Set(result.orphans.map((orphan) => orphan.publicId)));
    } catch (error) {
      customSwal.fire({ icon: 'error', title: 'Scan failed', text: error.message });
    } finally {
      setScanning(false);
    }
  };

  const toggle = (publicId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });
  };

  const handleDelete = async () => {
    const publicIds = [...selected];
    const { isConfirmed } = await customSwal.fire({
      title: `Delete ${publicIds.length} ${publicIds.length === 1 ? 'image' : 'images'}?`,
      text: 'They will be permanently removed from Cloudinary. Anything an item still uses is skipped.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
    });
    if (!isConfirmed) return;

    setDeleting(true);
    try {
      const { deleted, skipped } = await deleteOrphanedImages(publicIds, token);
      setScan((prev) => {
        const orphans = prev.orphans.filter((orphan) => !deleted.includes(orphan.publicId));
        return { ...prev, orphans, totalBytes: orphans.reduce((sum, orphan) => sum + (orphan.bytes || 0), 0) };
      });
      setSelected(new Set());
      customSwal.fire({
        icon: 'success',
        title: 'Cleanup finished',
        text: `Deleted ${deleted.length}${skipped.length ? `, skipped ${skipped.length} (in use or failed)` : ''}.`,
      });
    } catch (error) {
      customSwal.fire({ icon: 'error', title: 'Cleanup failed', text: error.message });
    } finally {
      setDeleting(false);
    }
  };

  const allSelected = scan && scan.orphans.length > 0 && selected.size === scan.orphans.length;

  return (
    <section className="mb-6 rounded-lg border border-gray-700 bg-gray-900/70 p-4 text-left">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-bold">Image storage cleanup</h3>
          <p className="text-sm text-gray-400">
            Finds images in the Cloudinary console folder that no console item uses (skipping anything uploaded in the last day).
          </p>
        </div>
        <button
          type="button"
          onClick={runScan}
          disabled={scanning}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {scanning ? 'Scanning…' : 'Scan for unused images'}
        </button>
      </div>

      {scan && (scan.orphans.length === 0 ? (
        <p className="mt-4 text-sm text-green-300">Checked {scan.scanned} images; nothing is unused.</p>
      ) : (
        <>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-300">
              {scan.orphans.length} unused of {scan.scanned} · {formatBytes(scan.totalBytes)}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSelected(allSelected ? new Set() : new Set(scan.orphans.map((o) => o.publicId)))}
                className="rounded-md bg-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-600"
              >
                {allSelected ? 'Select none' : 'Select all'}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={selected.size === 0 || deleting}
                className="rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : `Delete selected (${selected.size})`}
              </button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-8">
            {scan.orphans.map((orphan) => (
              <label
                key={orphan.publicId}
                title={orphan.publicId}
                className={`relative block cursor-pointer overflow-hidden rounded border-2 ${
                  selected.has(orphan.publicId) ? 'border-red-500' : 'border-transparent'
                }`}
              >
                <input
                  type="checkbox"
                  className="absolute left-1 top-1"
                  checked={selected.has(orphan.publicId)}
                  onChange={() => toggle(orphan.publicId)}
                />
                <img src={orphan.url} alt="" loading="lazy" className="aspect-square w-full object-cover" />
                <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 text-[10px] text-gray-200">
                  {formatBytes(orphan.bytes)}
                </span>
              </label>
            ))}
          </div>
        </>
      ))}
    </section>
  );
};

export default AdminImageCleanup;
